import { useEffect, useRef, useState, useCallback } from 'react';
import { shouldSyncList } from '../utils/optimisticListSync';

type CompareFields<T> = string[] | ((item: T, localItem: T) => boolean);

interface UseOptimisticListOptions<T> {
  compareFields?: CompareFields<T>;
}

/**
 * Manages optimistic UI updates with smart server-data synchronization.
 * Only updates local state when server data actually changes.
 */
export function useOptimisticList<T extends Record<string, any> & { id: number | string }>(
  serverData: T[] | null | undefined,
  options: UseOptimisticListOptions<T> = {}
) {
  const { compareFields } = options;
  const [localList, setLocalList] = useState<T[]>(serverData || []);
  const localListRef = useRef<T[]>(localList);
  // Ref keeps the sync effect stable when callers pass an inline array literal.
  const compareFieldsRef = useRef<CompareFields<T> | undefined>(compareFields);
  compareFieldsRef.current = compareFields;

  useEffect(() => {
    localListRef.current = localList;
  }, [localList]);

  useEffect(() => {
    if (
      shouldSyncList(serverData, localListRef.current, {
        compareFields: compareFieldsRef.current,
      })
    ) {
      setLocalList(serverData || []);
    }
  }, [serverData]);

  const updateItem = useCallback((id: number | string, patch: Record<string, any>) => {
    setLocalList((prev) =>
      prev.map((item) => (item.id === id ? ({ ...item, ...patch } as T) : item))
    );
  }, []);

  const restoreItem = useCallback((restoredItem: T | null | undefined) => {
    if (!restoredItem) return;
    setLocalList((prev) => prev.map((item) => (item.id === restoredItem.id ? restoredItem : item)));
  }, []);

  return {
    localList,
    updateItem,
    restoreItem,
    listRef: localListRef,
  };
}
